import { FC, useMemo } from 'react';

import dayjs from 'dayjs';
import { Swords, Trophy } from 'lucide-react';
import { isNullish } from 'remeda';
import useSWR from 'swr';

import css from './TournamentHistory.module.css';

import { userProfileService } from 'v2/api/userProfileService';
import { DataTable } from 'v2/components/dataTable/DataTable';
import { Loader } from 'v2/components/loader/Loader';
import { ProfileCard } from 'v2/components/profileCard/ProfileCard';
import { LONG_DASH } from 'v2/constants';
import { usePagination } from 'v2/hooks/usePagination';
import { TableColumn } from 'v2/types';
import { TournamentHistoryDto, UserStatisticDto } from 'v2/types/UserProfileModel';

type TournamentHistoryRow = TournamentHistoryDto & { id: string };

interface TournamentHistoryProps {
  userId: string;
  stats?: UserStatisticDto;
}

export const TournamentHistory: FC<TournamentHistoryProps> = ({ userId, stats }) => {
  const { page, pageSize, onPageChange } = usePagination({ defaultPageSize: 10 });

  const { data, isLoading } = useSWR(
    ['getUserTournamentHistory', page, pageSize, userId],
    userProfileService.getUserTournamentHistory,
    { keepPreviousData: true }
  );

  const rows: TournamentHistoryRow[] = useMemo(
    () =>
      data?.result.map((row, i) => ({
        ...row,
        id: `${row.tournamentId}-${row.startDate}-${i}`,
      })) ?? [],
    [data?.result]
  );

  const columns: TableColumn<TournamentHistoryRow>[] = useMemo(
    () => [
      {
        key: 'date',
        label: 'Date',
        render: (row) => `${dayjs(row.startDate).format('YYYY-MM-DD')} – ${dayjs(row.endDate).format('YYYY-MM-DD')}`,
      },
      {
        key: 'tournament',
        label: 'Tournament',
        render: (row) => row.tournamentName,
      },
      {
        key: 'place',
        label: 'Place',
        align: 'center',
        render: (row) => {
          if (row.place === -1) return LONG_DASH;

          if (row.place <= 3) {
            return (
              <span className={css.placeWithTrophy}>
                <Trophy height={16} width={16} /> #{row.place}
              </span>
            );
          }

          return `#${row.place}`;
        },
      },
      {
        key: 'prize',
        label: 'Prize',
        align: 'right',
        render: (row) => row.prize ?? LONG_DASH,
      },
    ],
    []
  );

  return (
    <ProfileCard
      className={css.tournamentHistoryCard}
      title="Tournament History"
      icon={<Swords height={18} width={18} />}
    >
      {isLoading && isNullish(data) ? (
        <Loader />
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          onPageChange={onPageChange}
          page={page}
          pageSize={pageSize}
          total={data?.total}
          withPagination
        />
      )}

      <div className={css.tournamentFooter}>
        <div>
          <span className={css.footerLabel}>DAILY VISITS (CURRENT TOURNAMENT)</span>

          <span className={css.footerValue}>{stats?.dailyVisits ?? LONG_DASH}</span>
        </div>

        <div>
          <span className={css.footerLabel}>LAST VISIT DATE (CURRENT TOURNAMENT)</span>

          <span className={css.footerValue}>
            {stats?.lastVisitDate ? dayjs(stats.lastVisitDate).format('YYYY-MM-DD') : LONG_DASH}
          </span>
        </div>
      </div>
    </ProfileCard>
  );
};
